import { RendererStyleFlags2 } from '@angular/core';

import { ViewDatesQuery } from '../../../generated/graphql.generated';
import { DateHighlight, DateStyle } from '../../shared/directives/highlight-dates.directive';

type ViewDateRow = NonNullable<ViewDatesQuery['viewDates']>[0];

/**
 * Builds the schedule date picker's per-day utilization-ring highlights
 * (the dashed conic ring around each calendar day cell, tier-coloured when
 * a forecast tier exists — Ray: "what we had before was right, this is the
 * same picker format as the new opportunity page").
 *
 * Mirrors the mapping the schedules page's removed inline picker used —
 * the crumb date picker feeds it from 3-month viewDates windows (one fetch
 * per browsed month), so rings show for every day in the panel including
 * adjacent-month days.
 */
export function buildUtilizationHighlights(rows: readonly ViewDateRow[]): DateHighlight[] {
    const dates: DateHighlight[] = [];

    for (const dateData of rows ?? []) {
        if (!dateData?.date) { continue; }

        const truckCapacity = dateData.truckCapacity || 0;
        const truckUtilization = truckCapacity > 0 ? (dateData.actualTrucksUsed / truckCapacity) : 0;

        const crewCapacity = dateData.crewCapacity || 0;
        const crewRequired = Math.max(dateData.actualCrewUsed, (dateData as any).actualCrewRequired || 0);
        const crewUtilization = crewCapacity > 0 ? (crewRequired / crewCapacity) : 0;

        const maxUtil = Math.max(truckUtilization, crewUtilization);
        const hasCapacity = truckCapacity > 0 || crewCapacity > 0;
        const availabilityRatio = hasCapacity ? Math.max(0, 1 - maxUtil) : 0;
        // The schedules page's thresholds (high ≤ 33% spare capacity): the
        // ring colours the old inline picker showed (0.66/0.33, CodeRabbit).
        const availabilityLevel = availabilityRatio > 0.66
            ? 'high'
            : availabilityRatio > 0.33
                ? 'medium'
                : 'low';
        const utilizationColor = availabilityLevel === 'high'
            ? 'var(--blue-500)'
            : availabilityLevel === 'medium'
                ? 'var(--orange-500)'
                : 'var(--red-500)';

        // Round to the nearest dashed line (8 lines total).
        const steps = Math.round(maxUtil * 8);
        const percent = (steps / 8) * 100;

        // Use the tier colour for the utilization ring when a forecast tier
        // is available; fall back to the availability colours otherwise.
        const tierColor = (dateData as any).prophet?.discount?.tier?.color;
        const ringColor = (typeof tierColor === 'string' && tierColor.trim().length > 0)
            ? tierColor
            : utilizationColor;

        const styles: DateStyle[] = [
            {
                style: '--utilization',
                value: `${Math.min(100, percent)}%`,
                flags: RendererStyleFlags2.DashCase,
            },
            {
                style: '--utilization-color',
                value: ringColor,
                flags: RendererStyleFlags2.DashCase,
            },
            {
                style: 'overflow',
                value: 'visible',
            }
        ];

        dates.push({
            date: dateData.date,
            styles,
            classes: ['utilization-border'],
        });
    }

    return dates;
}